/**
 * LoopNest Multi-Compute Wallet Service
 *
 * Unified compute wallet for:
 *   • Video generation (video_seconds)
 *   • Music generation (music_generations)
 *   • Game generation (game_generations)
 *
 * All functions are atomic transactions via Supabase RPC.
 * Platform owner (365ddevotional@gmail.com) bypasses all limits.
 */

import { supabase } from '../lib/supabase';
import { isPlatformOwnerEmail } from '../utils/adminOverride';

// ─── PLAN DEFINITIONS ────────────────────────────────────────────────────────

export const COMPUTE_PLANS = {
  free: {
    label: 'Free',
    price: '$0',
    videoSeconds: 120,
    maxVideoSeconds: 30,
    musicGenerations: 2,
    gameGenerations: 1,
    watermark: true,
    hdExport: false,
    priority: false,
    resetInterval: 'daily',
  },
  creator: {
    label: 'Creator',
    price: '$12/month',
    videoSeconds: 1800,
    maxVideoSeconds: 60,
    musicGenerations: 20,
    gameGenerations: 10,
    watermark: false,
    hdExport: true,
    priority: false,
    resetInterval: 'monthly',
  },
  pro: {
    label: 'Creator',
    price: '$12/month',
    videoSeconds: 1800,
    maxVideoSeconds: 60,
    musicGenerations: 20,
    gameGenerations: 10,
    watermark: false,
    hdExport: true,
    priority: false,
    resetInterval: 'monthly',
  },
  studio: {
    label: 'Studio',
    price: '$29/month',
    videoSeconds: 7200,
    maxVideoSeconds: 180,
    musicGenerations: 100,
    gameGenerations: 40,
    watermark: false,
    hdExport: true,
    priority: true,
    resetInterval: 'monthly',
  },
  pro_creator: {
    label: 'Pro Creator',
    price: '$59/month',
    videoSeconds: 20000,
    maxVideoSeconds: 300,
    musicGenerations: 300,
    gameGenerations: 100,
    watermark: false,
    hdExport: true,
    priority: true,
    resetInterval: 'monthly',
  },
  enterprise: {
    label: 'Pro Creator',
    price: '$59/month',
    videoSeconds: 20000,
    maxVideoSeconds: 300,
    musicGenerations: 300,
    gameGenerations: 100,
    watermark: false,
    hdExport: true,
    priority: true,
    resetInterval: 'monthly',
  },
};

// ─── DAILY FREE CREDITS ───────────────────────────────────────────────────────

export const DAILY_FREE_CREDITS = {
  video_seconds: 10,
  music_generations: 1,
  game_generations: 1,
};

// ─── TOP-UP PACKS ─────────────────────────────────────────────────────────────

export const COMPUTE_TOPUP_PACKS = [
  // Video packs
  { id: 'video_1000s', type: 'video_seconds', amount: 1000, price: 5, priceDisplay: '$5', label: '1,000 Video Seconds', icon: 'Video', color: '#6366F1' },
  { id: 'video_5000s', type: 'video_seconds', amount: 5000, price: 20, priceDisplay: '$20', label: '5,000 Video Seconds', icon: 'Video', color: '#6366F1', popular: true },
  // Music packs
  { id: 'music_10', type: 'music_generations', amount: 10, price: 4, priceDisplay: '$4', label: '10 Music Generations', icon: 'Music', color: '#8B5CF6' },
  { id: 'music_50', type: 'music_generations', amount: 50, price: 15, priceDisplay: '$15', label: '50 Music Generations', icon: 'Music', color: '#8B5CF6', popular: true },
  // Game packs
  { id: 'game_10', type: 'game_generations', amount: 10, price: 6, priceDisplay: '$6', label: '10 Game Generations', icon: 'Gamepad2', color: '#10B981' },
  { id: 'game_50', type: 'game_generations', amount: 50, price: 25, priceDisplay: '$25', label: '50 Game Generations', icon: 'Gamepad2', color: '#10B981', popular: true },
];

// ─── PLATFORM OWNER BYPASS ────────────────────────────────────────────────────

const OWNER_WALLET = {
  videoSecondsRemaining: 999999,
  videoSecondsUsed: 0,
  videoSecondsQuota: 999999,
  maxVideoSeconds: 300,
  musicRemaining: 999999,
  musicUsed: 0,
  musicQuota: 999999,
  gameRemaining: 999999,
  gameUsed: 0,
  gameQuota: 999999,
  watermark: false,
  hdExport: true,
  priority: true,
  planLabel: 'Platform Owner',
  tier: 'enterprise',
  isPlatformOwner: true,
};

// ─── GET COMPUTE WALLET STATUS ────────────────────────────────────────────────

/**
 * Get the full compute wallet status for a user.
 * Returns all three compute buckets: video, music, game.
 */
export async function getComputeWalletStatus(userId, userTier = 'free', userEmail = '') {
  if (isPlatformOwnerEmail(userEmail)) return OWNER_WALLET;

  try {
    const { data, error } = await supabase?.rpc('get_compute_wallet_status', {
      p_user_id: userId,
      p_tier: userTier,
    });

    if (error) throw error;

    return {
      videoSecondsRemaining: data?.video_seconds_remaining ?? 0,
      videoSecondsUsed: data?.video_seconds_used ?? 0,
      videoSecondsQuota: data?.video_seconds_quota ?? 120,
      maxVideoSeconds: data?.max_video_seconds ?? 30,
      musicRemaining: data?.music_remaining ?? 0,
      musicUsed: data?.music_used ?? 0,
      musicQuota: data?.music_quota ?? 2,
      gameRemaining: data?.game_remaining ?? 0,
      gameUsed: data?.game_used ?? 0,
      gameQuota: data?.game_quota ?? 1,
      watermark: data?.watermark ?? true,
      hdExport: data?.hd_export ?? false,
      priority: data?.priority ?? false,
      planLabel: data?.plan_label ?? 'Free',
      tier: data?.tier ?? userTier,
      lastDailyReset: data?.last_daily_reset,
      lastMonthlyReset: data?.last_monthly_reset,
    };
  } catch (err) {
    console.error('[ComputeWallet] getComputeWalletStatus error:', err?.message);
    const plan = COMPUTE_PLANS?.[userTier] || COMPUTE_PLANS?.free;
    return {
      videoSecondsRemaining: plan?.videoSeconds,
      videoSecondsUsed: 0,
      videoSecondsQuota: plan?.videoSeconds,
      maxVideoSeconds: plan?.maxVideoSeconds,
      musicRemaining: plan?.musicGenerations,
      musicUsed: 0,
      musicQuota: plan?.musicGenerations,
      gameRemaining: plan?.gameGenerations,
      gameUsed: 0,
      gameQuota: plan?.gameGenerations,
      watermark: plan?.watermark,
      hdExport: plan?.hdExport,
      priority: plan?.priority,
      planLabel: plan?.label,
      tier: userTier,
    };
  }
}

// ─── VIDEO SECONDS FUNCTIONS ──────────────────────────────────────────────────

/**
 * Check if user has enough video seconds quota.
 */
export async function checkVideoSecondsQuota(userId, userTier = 'free', requestedSeconds = 30, userEmail = '') {
  if (isPlatformOwnerEmail(userEmail)) {
    return { allowed: true, reason: 'platform_owner', remaining: 999999, quota: 999999, maxVideoSeconds: 300 };
  }

  const wallet = await getComputeWalletStatus(userId, userTier, userEmail);
  const plan = COMPUTE_PLANS?.[userTier] || COMPUTE_PLANS?.free;

  if (requestedSeconds > wallet?.maxVideoSeconds) {
    return {
      allowed: false,
      reason: 'duration_exceeds_plan',
      message: `Your ${plan?.label} plan allows max ${wallet?.maxVideoSeconds}s per video. Requested: ${requestedSeconds}s.`,
      remaining: wallet?.videoSecondsRemaining,
      quota: wallet?.videoSecondsQuota,
      maxVideoSeconds: wallet?.maxVideoSeconds,
    };
  }

  if (requestedSeconds > wallet?.videoSecondsRemaining) {
    return {
      allowed: false,
      reason: 'quota_exceeded',
      message: `You have ${wallet?.videoSecondsRemaining}s remaining. This video requires ${requestedSeconds}s. Upgrade or wait for reset.`,
      remaining: wallet?.videoSecondsRemaining,
      quota: wallet?.videoSecondsQuota,
      maxVideoSeconds: wallet?.maxVideoSeconds,
    };
  }

  return {
    allowed: true,
    reason: 'ok',
    remaining: wallet?.videoSecondsRemaining,
    quota: wallet?.videoSecondsQuota,
    maxVideoSeconds: wallet?.maxVideoSeconds,
  };
}

/**
 * Atomically deduct video seconds from wallet.
 */
export async function deductVideoSeconds(userId, userTier = 'free', requestedSeconds = 30, jobId = null, userEmail = '') {
  if (isPlatformOwnerEmail(userEmail)) {
    return { success: true, remaining: 999999, message: 'Platform owner bypass.' };
  }

  try {
    const { data, error } = await supabase?.rpc('check_and_deduct_video_seconds_v2', {
      p_user_id: userId,
      p_tier: userTier,
      p_requested_secs: requestedSeconds,
      p_job_id: jobId,
    });

    if (error) throw error;

    return {
      success: data?.allowed === true,
      reason: data?.reason,
      message: data?.message,
      remaining: data?.remaining ?? 0,
      quota: data?.quota ?? 0,
      secondsDeducted: data?.seconds_deducted,
    };
  } catch (err) {
    console.error('[ComputeWallet] deductVideoSeconds error:', err?.message);
    return { success: false, message: err?.message || 'Deduction failed.' };
  }
}

/**
 * Refund video seconds on failed generation.
 */
export async function refundVideoSeconds(userId, seconds, jobId = null, userEmail = '') {
  if (isPlatformOwnerEmail(userEmail)) return { success: true };

  try {
    const { data, error } = await supabase?.rpc('refund_video_seconds_v2', {
      p_user_id: userId,
      p_seconds: seconds,
      p_job_id: jobId,
    });
    if (error) throw error;
    return { success: true, refunded: data?.refunded };
  } catch (err) {
    console.error('[ComputeWallet] refundVideoSeconds error:', err?.message);
    return { success: false, message: err?.message };
  }
}

// ─── MUSIC GENERATION FUNCTIONS ───────────────────────────────────────────────

/**
 * Check if user has music generation quota remaining.
 */
export async function checkMusicQuota(userId, userTier = 'free', userEmail = '') {
  if (isPlatformOwnerEmail(userEmail)) {
    return { allowed: true, reason: 'platform_owner', remaining: 999999, quota: 999999 };
  }

  const wallet = await getComputeWalletStatus(userId, userTier, userEmail);

  if (wallet?.musicRemaining <= 0) {
    const plan = COMPUTE_PLANS?.[userTier] || COMPUTE_PLANS?.free;
    return {
      allowed: false,
      reason: 'music_quota_exceeded',
      message: `You have used all ${wallet?.musicQuota} music generations for this period. Upgrade to generate more.`,
      remaining: 0,
      quota: wallet?.musicQuota,
    };
  }

  return { allowed: true, reason: 'ok', remaining: wallet?.musicRemaining, quota: wallet?.musicQuota };
}

/**
 * Atomically deduct one music generation from wallet.
 */
export async function deductMusicGeneration(userId, userTier = 'free', jobId = null, userEmail = '') {
  if (isPlatformOwnerEmail(userEmail)) {
    return { success: true, remaining: 999999, message: 'Platform owner bypass.' };
  }

  try {
    const { data, error } = await supabase?.rpc('check_and_deduct_music_generation', {
      p_user_id: userId,
      p_tier: userTier,
      p_job_id: jobId,
    });

    if (error) throw error;

    return {
      success: data?.allowed === true,
      reason: data?.reason,
      message: data?.message,
      remaining: data?.remaining ?? 0,
      quota: data?.quota ?? 0,
    };
  } catch (err) {
    console.error('[ComputeWallet] deductMusicGeneration error:', err?.message);
    return { success: false, message: err?.message || 'Music deduction failed.' };
  }
}

/**
 * Refund one music generation on failure.
 */
export async function refundMusicGeneration(userId, jobId = null, userEmail = '') {
  if (isPlatformOwnerEmail(userEmail)) return { success: true };

  try {
    const { data, error } = await supabase?.rpc('refund_music_generation', {
      p_user_id: userId,
      p_job_id: jobId,
    });
    if (error) throw error;
    return { success: true, refunded: data?.refunded };
  } catch (err) {
    console.error('[ComputeWallet] refundMusicGeneration error:', err?.message);
    return { success: false, message: err?.message };
  }
}

// ─── GAME GENERATION FUNCTIONS ────────────────────────────────────────────────

/**
 * Check if user has game generation quota remaining.
 */
export async function checkGameQuota(userId, userTier = 'free', userEmail = '') {
  if (isPlatformOwnerEmail(userEmail)) {
    return { allowed: true, reason: 'platform_owner', remaining: 999999, quota: 999999 };
  }

  const wallet = await getComputeWalletStatus(userId, userTier, userEmail);

  if (wallet?.gameRemaining <= 0) {
    return {
      allowed: false,
      reason: 'game_quota_exceeded',
      message: `You have used all ${wallet?.gameQuota} game generations for this period. Upgrade to generate more.`,
      remaining: 0,
      quota: wallet?.gameQuota,
    };
  }

  return { allowed: true, reason: 'ok', remaining: wallet?.gameRemaining, quota: wallet?.gameQuota };
}

/**
 * Atomically deduct one game generation from wallet.
 */
export async function deductGameGeneration(userId, userTier = 'free', jobId = null, userEmail = '') {
  if (isPlatformOwnerEmail(userEmail)) {
    return { success: true, remaining: 999999, message: 'Platform owner bypass.' };
  }

  try {
    const { data, error } = await supabase?.rpc('check_and_deduct_game_generation', {
      p_user_id: userId,
      p_tier: userTier,
      p_job_id: jobId,
    });

    if (error) throw error;

    return {
      success: data?.allowed === true,
      reason: data?.reason,
      message: data?.message,
      remaining: data?.remaining ?? 0,
      quota: data?.quota ?? 0,
    };
  } catch (err) {
    console.error('[ComputeWallet] deductGameGeneration error:', err?.message);
    return { success: false, message: err?.message || 'Game deduction failed.' };
  }
}

/**
 * Refund one game generation on failure.
 */
export async function refundGameGeneration(userId, jobId = null, userEmail = '') {
  if (isPlatformOwnerEmail(userEmail)) return { success: true };

  try {
    const { data, error } = await supabase?.rpc('refund_game_generation', {
      p_user_id: userId,
      p_job_id: jobId,
    });
    if (error) throw error;
    return { success: true, refunded: data?.refunded };
  } catch (err) {
    console.error('[ComputeWallet] refundGameGeneration error:', err?.message);
    return { success: false, message: err?.message };
  }
}

// ─── TOP-UP CREDITS ───────────────────────────────────────────────────────────

/**
 * Add compute credits via top-up purchase.
 */
export async function topupComputeCredits(userId, computeType, amount, reason = 'Top-up purchase', sessionId = null) {
  try {
    const { data, error } = await supabase?.rpc('topup_compute_credits', {
      p_user_id: userId,
      p_compute_type: computeType,
      p_amount: amount,
      p_reason: reason,
      p_session_id: sessionId,
    });
    if (error) throw error;
    return { success: data?.success, amountAdded: data?.amount_added, newBalance: data?.new_balance };
  } catch (err) {
    console.error('[ComputeWallet] topupComputeCredits error:', err?.message);
    return { success: false, message: err?.message };
  }
}

// ─── TRANSACTION HISTORY ──────────────────────────────────────────────────────

/**
 * Get compute credit transaction history for a user.
 */
export async function getComputeTransactionHistory(userId, computeType = null, limit = 30) {
  let query = supabase
    ?.from('compute_credit_transactions')
    ?.select('*')
    ?.eq('user_id', userId)
    ?.order('created_at', { ascending: false })
    ?.limit(limit);

  if (computeType) query = query?.eq('compute_type', computeType);

  const { data, error } = await query;
  if (error) return [];

  return (data || [])?.map((t) => ({
    id: t?.id,
    computeType: t?.compute_type,
    action: t?.action,
    amount: t?.amount,
    balanceAfter: t?.balance_after,
    reason: t?.reason,
    jobId: t?.job_id,
    createdAt: t?.created_at,
  }));
}

// ─── COMMUNITY GALLERY ────────────────────────────────────────────────────────

/**
 * Publish a creation to the community gallery.
 */
export async function publishToGallery(params) {
  const { userId, jobId, title, description, generationType, thumbnailUrl, outputUrl, prompt, allowRemix = true, tags = [] } = params;

  const { data, error } = await supabase?.from('community_gallery')?.insert({
    user_id: userId,
    job_id: jobId,
    title,
    description,
    generation_type: generationType,
    thumbnail_url: thumbnailUrl,
    output_url: outputUrl,
    prompt,
    allow_remix: allowRemix,
    is_published: true,
    tags,
  })?.select()?.single();

  if (error) throw new Error(`Failed to publish to gallery: ${error?.message}`);
  return data;
}

/**
 * Get public community gallery items.
 */
export async function getCommunityGallery(options = {}) {
  const { generationType = null, limit = 24, offset = 0, sortBy = 'created_at' } = options;

  let query = supabase
    ?.from('community_gallery')
    ?.select('*, user_profiles!inner(full_name, avatar_url)')
    ?.eq('is_published', true)
    ?.order(sortBy, { ascending: false })
    ?.range(offset, offset + limit - 1);

  if (generationType) query = query?.eq('generation_type', generationType);

  const { data, error } = await query;
  if (error) return [];

  return (data || [])?.map((item) => ({
    id: item?.id,
    userId: item?.user_id,
    jobId: item?.job_id,
    title: item?.title,
    description: item?.description,
    generationType: item?.generation_type,
    thumbnailUrl: item?.thumbnail_url,
    outputUrl: item?.output_url,
    prompt: item?.prompt,
    allowRemix: item?.allow_remix,
    remixCount: item?.remix_count,
    viewCount: item?.view_count,
    likeCount: item?.like_count,
    tags: item?.tags,
    createdAt: item?.created_at,
    creator: {
      name: item?.user_profiles?.full_name || 'Anonymous',
      avatar: item?.user_profiles?.avatar_url,
    },
  }));
}
